/**
 * Throwaway tools for exercising the tool permission system before real tools
 * exist. `SampleTool` is a single page; `SampleWorkspace` owns sub-routes under
 * `/tools/sample-workspace`. Deleting this file, its two catalog entries, its
 * two registry entries, and `routes/v1/sampleTools.js` removes them entirely.
 */
import React, { useState } from 'react';
import {
  Alert, Badge, Button, Card, Table,
} from 'react-bootstrap';
import { Link, Route, Switch } from 'react-router-dom';
import { PageLayout } from '../../../dorian-shared/types/pageLayout/PageLayout';
import { api } from '../../api';
import { ToolComponentProps } from '../ToolPage/ToolPage';
import { PageWrapper } from '../../ui/PageWrapper';

interface WorkspaceItem {
  id: string;
  name: string;
  status: string;
}

const WORKSPACE_ITEMS: WorkspaceItem[] = [
  { id: 'alpha', name: 'Alpha', status: 'Draft' },
  { id: 'beta', name: 'Beta', status: 'In review' },
  { id: 'gamma', name: 'Gamma', status: 'Published' },
];

function backLayout(title: string, goBack: () => void): PageLayout {
  return {
    header: { title },
    sidebar: {
      nav: [
        {
          title: 'Back',
          action: goBack,
          variant: 'secondary',
          disabled: false,
        },
      ],
    },
  };
}

function RoleBadge(props: { toolRole: ToolComponentProps['toolRole'] }) {
  const { toolRole } = props;
  return (
    <Badge variant={toolRole === 'editor' ? 'primary' : 'secondary'}>
      {toolRole || 'no access'}
    </Badge>
  );
}

export function SampleTool(props: ToolComponentProps) {
  const {
    auth, toolKey, toolRole, history,
  } = props;
  const user = auth.getUser();
  const isEditor = toolRole === 'editor';
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const callEndpoint = async (method: 'get' | 'post') => {
    setResult(null);
    setError(null);
    try {
      const response = method === 'get'
        ? await api.get(`/v1/tools/${toolKey}/status`)
        : await api.post(`/v1/tools/${toolKey}/status`, {});
      setResult(response.data.message);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Request failed');
    }
  };

  return (
    <PageWrapper user={user} page={backLayout('Sample Tool', () => history.goBack())}>
      <Card className="mt-3">
        <Card.Body>
          <Card.Title>
            Sample Tool
            {' '}
            <RoleBadge toolRole={toolRole} />
          </Card.Title>
          <Card.Text>
            Placeholder tool for exercising tool permissions. The read action is
            available to viewers and editors; the write action is editor-only and
            is enforced by the API, not just by this button being disabled.
          </Card.Text>

          {result && <Alert variant="success">{result}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}

          <Button
            variant="outline-primary"
            className="mr-2"
            onClick={() => callEndpoint('get')}
          >
            Read action (viewer+)
          </Button>
          <Button
            variant="primary"
            onClick={() => callEndpoint('post')}
            disabled={!isEditor}
          >
            Write action (editor only)
          </Button>
        </Card.Body>
      </Card>
    </PageWrapper>
  );
}

function SampleWorkspaceList(props: ToolComponentProps) {
  const {
    auth, toolRole, match, history,
  } = props;
  const user = auth.getUser();
  const isEditor = toolRole === 'editor';

  return (
    <PageWrapper user={user} page={backLayout('Sample Workspace', () => history.goBack())}>
      <div className="mt-3">
        <h5>
          Items
          {' '}
          <RoleBadge toolRole={toolRole} />
        </h5>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {WORKSPACE_ITEMS.map((item) => (
              <tr key={item.id}>
                <td>{item.name}</td>
                <td>{item.status}</td>
                <td>
                  <Button
                    as={Link}
                    to={`${match.url}/${item.id}`}
                    size="sm"
                    variant="outline-primary"
                  >
                    Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Button variant="primary" disabled={!isEditor}>
          New item (editor only)
        </Button>
      </div>
    </PageWrapper>
  );
}

function SampleWorkspaceDetail(props: ToolComponentProps & { itemId: string }) {
  const {
    auth, toolRole, itemId, history,
  } = props;
  const user = auth.getUser();
  const isEditor = toolRole === 'editor';
  const item = WORKSPACE_ITEMS.find((entry) => entry.id === itemId) || null;
  const title = item ? `Sample Workspace — ${item.name}` : 'Sample Workspace';

  return (
    <PageWrapper user={user} page={backLayout(title, () => history.goBack())}>
      {!item ? (
        <Alert variant="warning" className="mt-3">
          No such item.
        </Alert>
      ) : (
        <Card className="mt-3">
          <Card.Body>
            <Card.Title>
              {item.name}
              {' '}
              <RoleBadge toolRole={toolRole} />
            </Card.Title>
            <Card.Text>
              Status:
              {' '}
              {item.status}
            </Card.Text>
            {isEditor ? (
              <Button variant="primary">Edit item</Button>
            ) : (
              <Alert variant="info" className="mb-0">
                Read-only. Editors can modify this item.
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}
    </PageWrapper>
  );
}

export function SampleWorkspace(props: ToolComponentProps) {
  const { match } = props;

  return (
    <Switch>
      <Route
        exact
        path={match.path}
        render={() => <SampleWorkspaceList {...props} />}
      />
      <Route
        path={`${match.path}/:itemId`}
        render={(routeProps: { match: { params: { itemId: string } } }) => (
          <SampleWorkspaceDetail {...props} itemId={routeProps.match.params.itemId} />
        )}
      />
    </Switch>
  );
}
